import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import {
    DragDropContext, Draggable, Droppable, DropResult,
} from "react-beautiful-dnd";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { BsPlusLg } from "react-icons/bs";
import { IoCloseSharp, IoEllipsisVertical } from "react-icons/io5";
import { BiCollapse, BiCollapseAlt } from "react-icons/bi";
import {
    ActionContextType, FormField, FormFieldType, FormFieldVisibility,
    Workflow,
    WorkflowInput, WorkflowServiceAction, WorkflowServiceChildAction,
    WorkflowServiceOutputParameter,
    WorkflowStep, WorkflowStepGroup, WorkflowStepInput,
    WorkflowStepOutputParameter,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import { ReplacementGroup } from "../replacement-group";
import Styles from "./Workflow.module.scss";
import WorkflowCardCreate from "./WorkflowCardCreate";

const ACTIONS_CREATED_ID = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:"
    + "(urn:fq:workflow-service:system,request-created),actions)";

const ACTIONS_CREATING_ID = "urn:fq:workflow-service-action-child:(urn:fq:workflow-service-action:"
    + "(urn:fq:workflow-service:system,request-creating),actions)";

const CREATING_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-creating)";
const CREATED_ID = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,request-created)";

const ACTION_MAP: Record<string, ActionContextType> = {
    [CREATING_ID]: ActionContextType.Request,
    [CREATED_ID]: ActionContextType.Ticket,
};

const ACTION_CREATING_REQUEST = {
    id: "",
    name: "Creating request",
    actionId: CREATING_ID,
    action: {
        id: "",
        name: "Request Creating",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_CREATING_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The automation to execute while the request is being created by the end user. "
        + "This can handle actions such conditionally showing or hiding fields.",
    parameters: [],
};
const ACTION_REQUEST_CREATED = {
    id: "",
    name: "Request Created",
    actionId: CREATED_ID,
    action: {
        id: "",
        name: "Request Created",
        description: "Request first created",
        allowedChildren: [
            {
                id: ACTIONS_CREATED_ID,
                label: "Actions",
                description: "",
            },
        ],
    },
    serviceId: "urn:fq:workflow-service:system",
    description: "The automations to execute after the user has created the request. This allows "
        + "you to take actions such as firing off a web hook.",
    parameters: [],
};

function createChildren() {
    const children: FormField[] = [];
    const creator = (
        row: WorkflowStepOutputParameter | WorkflowServiceOutputParameter | FormField,
    ) => {
        if (row.type === FormFieldType.Users) {
            children.push({
                ...(row as FormField),
                id: row.id,
                name: row.name,
                description: row.description,
                type: row.type,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-users-field:(${row.id},names)`,
                name: `${row.name} Name List`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-users-field:(${row.id},names-emails)`,
                name: `${row.name} Name and emails List`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
        } else if (row.type === FormFieldType.User) {
            children.push({
                ...(row as FormField),
                id: row.id,
                name: row.name,
                description: row.description,
                type: row.type,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},name)`,
                name: `${row.name} Name`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},firstName)`,
                name: `${row.name} First Name`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},lastName)`,
                name: `${row.name} Last Name`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
            children.push({
                id: `urn:fq:workflow-user-field:(${row.id},email)`,
                name: `${row.name} Email`,
                description: row.description,
                type: FormFieldType.Text,
                isSystemField: true,
                visibility: FormFieldVisibility.Private,
            });
        } else {
            children.push({
                ...(row as FormField),
                id: row.id,
                name: row.name,
                description: row.description,
                type: row.type,
                isSystemField: false,
                visibility: FormFieldVisibility.Private,
            });
        }
    };

    return { children, creator };
}

function WorkflowCard({
    item, className, onEdit, onDelete, level, canDragDrop,
}: {
    item: WorkflowStep;
    className?: string;
    onEdit: () => void;
    onDelete: () => void;
    level: number;
    canDragDrop?: boolean;

}): ReactElement {
    const [isHovering, setIsHovering] = useState(false);

    return (
        <Card
            className={`${className} cursor-pointer ${isHovering ? "shadow" : ""}`}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onFocus={() => setIsHovering(true)}
            onBlur={() => setIsHovering(false)}
        >
            <div className="px-3 py-2 border-1 rounded">
                <div className="mb-0 ">

                    <div className="d-flex card-title align-items-center justify-content-between">
                        <OverlayTrigger
                            placement="right"
                            delay={{ show: 250, hide: 0 }}
                            overlay={(
                                <Tooltip id={`tooltip-${item.id}`}>
                                    {item.description ?? item.action?.description}
                                </Tooltip>
                            )}
                        >
                            <div className="d-flex">
                                {canDragDrop && (
                                    <div className="cursor-move">
                                        <IoEllipsisVertical className="text-light ms-n2 me-2" />
                                    </div>
                                )}

                                <button className={`btn ${Styles.card} btn-link p-0 m-0`} type="button" onClick={onEdit}>
                                    <h6 className={`m-0 mt-1 me-3 w-100 ${Styles.label}`}>
                                        {item.name ?? item.action?.name}
                                    </h6>
                                </button>
                            </div>
                        </OverlayTrigger>

                        {level > 0 && item.action?.id && (
                            <div>
                                <button className="btn btn-link p-0 m-0 ms-2  text-light" type="button" onClick={onDelete}>
                                    <small>
                                        <IoCloseSharp />
                                    </small>
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </Card>
    );
}
WorkflowCard.defaultProps = {
    className: "",
    canDragDrop: false,
};

function WorkflowRow({
    item, level, onUpdate, onEdit, onDelete, input,
    allowedActionIds, type, index, isDragging, className,
    isDraggingOver, draggingId, rootStep,
}: {
    item: WorkflowStep;
    index: number;
    level: number;
    rootStep: WorkflowStep;
    onUpdate: () => void;
    onEdit: () => void;
    onDelete: () => void;
    input: ReplacementGroup[];
    allowedActionIds: string[];
    type: ActionContextType;
    isDragging: boolean;
    isDraggingOver?: boolean;
    className?: string;
    draggingId: string | undefined;
}): ReactElement {
    const [isHover, setIsHovering] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<{
        action: WorkflowStep;
        group: WorkflowStepGroup;
    }>();

    const isHovering = isDragging || isHover || !item.action?.id;

    const [childAction, setChildAction] = useState<WorkflowServiceChildAction>();
    const [editInput, setEditInput] = useState<ReplacementGroup[]>();
    const [editItem, setEditItem] = useState<WorkflowStep>();
    const [allowedChildren, setAllowedChildren] = useState<string[]>();

    // const createClasses = ["btn border p-2 px-3 rounded-pill ", Styles["left-line"], "menu-small"];

    const hasChildren = item.children?.some(row => row.children?.isNotEmpty);
    // const lineClass = hasChildren ? Styles["left-none"] : Styles["left-create"];
    // createClasses.push(lineClass);

    const handleEditChild = (action: WorkflowStep, child: WorkflowServiceChildAction, childInput: ReplacementGroup[]) => {
        setEditItem(action);
        setChildAction(child);
        setEditInput(childInput);
        setAllowedChildren(undefined);
    };

    const handleDeleteChild = (action: WorkflowStep, group: WorkflowStepGroup) => {
        setDeleteCandidate({ action, group });
        setAllowedChildren(undefined);
    };

    const confirmDelete = () => {
        if (!deleteCandidate) {
            return;
        }
        const { action, group } = deleteCandidate;
        if (!group.children) {
            return;
        }
        const deleteIndex = group.children.findIndex(row => row === action);
        if (deleteIndex >= 0) {
            group.children.splice(deleteIndex, 1);
            onUpdate();
        }
        setDeleteCandidate(undefined);
    };

    const cardCreate = (
        <WorkflowCardCreate
            show={childAction != null}
            onHide={() => {
                setChildAction(undefined);
                setEditItem(undefined);
            }}
            type={type}
            workflow={rootStep}
            allowedActionIds={allowedChildren ?? childAction?.allowedActionIds ?? []}
            action={editItem}
            input={editInput ?? input ?? []}
            serviceAction={editItem?.action as WorkflowServiceAction}
            onSave={card => {
                // eslint-disable-next-line no-param-reassign
                item.children = item.children ?? [];
                let container = item.children.find(child => child.groupId === childAction?.id);
                if (!container) {
                    container = {
                        groupId: childAction?.id,
                        children: [],
                    };
                    item.children.push(container);
                }
                if (!container.children) {
                    container.children = [];
                }
                const saveIndex = container.children.findIndex(child => child === editItem);

                if (saveIndex >= 0) {
                    container.children[saveIndex] = card;
                } else {
                    container.children?.push(card);
                }

                setChildAction(undefined);
                setEditItem(undefined);
                setEditInput(undefined);
                onUpdate();
            }}
        />
    );

    const container = (
        <>
            {deleteCandidate && (
                <Confirm
                    shouldShow={deleteCandidate != null}
                    onHide={() => setDeleteCandidate(undefined)}
                    confirmText="Delete"
                    confirmVariant="danger"
                    onConfirm={confirmDelete}
                >
                    <span>
                        Are you sure you want to delete this
                        {" "}
                        &quot;
                        {deleteCandidate.action.name}
                        &quot;
                        {" "}
                        action?
                    </span>
                </Confirm>
            )}
            <div
                className={`
                    ${item.action?.id === "" ? "d-flex" : "d-flex"}
                    ${!item.children ? "align-items-start" : "align-items-start"}`}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onFocus={() => setIsHovering(true)}
                onBlur={() => setIsHovering(false)}
            >
                <div className="m-0">
                    <WorkflowCard
                        item={item}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        level={level}
                        canDragDrop={level !== 0}
                    />
                </div>

                <div className="">
                    {draggingId !== item.id
                        && (
                            hasChildren || ((isHovering || isDragging)
                            && (item.allowedChildren?.isNotEmpty || item.action?.allowedChildren?.isNotEmpty)))
                        && (
                            <div
                                className={`${Styles["left-line"]} ms-3`}
                            >
                                {(item.allowedChildren ?? item.action?.allowedChildren)?.map(child => {
                                    const group = item.children?.find(groupChild => groupChild.groupId === child.id);
                                    if (!isHovering && !group) {
                                        return "";
                                    }

                                    let allowedChildrenIds: string[];
                                    if (allowedActionIds?.isNotEmpty && child.allowedActionIds?.isNotEmpty) {
                                        allowedChildrenIds = allowedActionIds.union(child.allowedActionIds);
                                    } else if (allowedActionIds?.isNotEmpty) {
                                        allowedChildrenIds = allowedActionIds;
                                    } else {
                                        allowedChildrenIds = child.allowedActionIds as string[];
                                    }

                                    const droppableId = `${item.id}/${child.id}`;
                                    return (
                                        <div
                                            className={`${Styles["left-line"]} ms-3 pb-4`}
                                            key={child.id}
                                        >
                                            <div
                                                className="d-flex align-items-center pt-2"
                                            >
                                                <button
                                                    type="button"
                                                    className="btn border p-1 px-2 rounded-pill me-3"
                                                    onClick={() => {
                                                        const allowedParentGroup = (item.allowedChildren
                                                            ?? item.action?.allowedChildren);

                                                        const { children, creator } = createChildren();
                                                        if (allowedParentGroup) {
                                                            allowedParentGroup.forEach(parentGroup => {
                                                                if (parentGroup.output) {
                                                                    parentGroup.output.forEach(creator);
                                                                }
                                                            });
                                                        }

                                                        const allowedParentInput = children.isEmpty ? [] : [{
                                                            id: item.id,
                                                            label: item.name,
                                                            description: item.description ?? "",
                                                            input: children,
                                                        } as ReplacementGroup];

                                                        setEditInput(input.concat(
                                                            allowedParentInput,
                                                            group?.children?.mapFilter(groupChild => {
                                                                const groupChildCreator = createChildren();

                                                                if (groupChild.action?.output?.isNotEmpty) {
                                                                    groupChild.action.output.forEach(groupChildCreator.creator);
                                                                }

                                                                if (groupChild.output?.isNotEmpty) {
                                                                    groupChild.output.forEach(groupChildCreator.creator);
                                                                }

                                                                return groupChildCreator.children.isEmpty ? undefined : {
                                                                    id: groupChild.id,
                                                                    label: groupChild.name,
                                                                    description: groupChild.description ?? "",
                                                                    input: groupChildCreator.children,
                                                                } as ReplacementGroup;
                                                            }) ?? [],
                                                        ));
                                                        setChildAction(child);
                                                        setAllowedChildren(allowedChildrenIds);
                                                    }}
                                                >
                                                    <BsPlusLg />
                                                </button>
                                                {child.label && (
                                                    <OverlayTrigger
                                                        placement="right"
                                                        delay={{ show: 250, hide: 0 }}
                                                        overlay={child.description ? (
                                                            <Tooltip id={`tooltip-action-${child.id}`}>
                                                                {child.description}
                                                            </Tooltip>
                                                        ) : <div />}
                                                    >
                                                        <h6 className="m-0">
                                                            {child.label}
                                                        </h6>
                                                    </OverlayTrigger>
                                                )}

                                            </div>

                                            <Droppable
                                                key={droppableId}
                                                droppableId={droppableId}
                                                mode="standard"
                                                direction="vertical"
                                                renderClone={(provided, snapshot, rubric) => (
                                                    <div
                                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                                        {...provided.draggableProps}
                                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                                        {...provided.dragHandleProps}
                                                        ref={provided.innerRef}
                                                    >
                                                        <WorkflowCard
                                                            item={group?.children?.find(row => row.id === rubric.draggableId) ?? {
                                                                actionId: "-1",
                                                                name: "Action",
                                                            }}
                                                            onEdit={onEdit}
                                                            onDelete={onDelete}
                                                            level={level}
                                                        />

                                                    </div>
                                                )}
                                            >
                                                {(dragChild, snapshot) => {
                                                    const classes = [Styles["droppable-target"]];
                                                    if (group?.children?.isNotEmpty) {
                                                        classes.push(Styles["left-line"], Styles["margin-start"], "pb-4");
                                                    }
                                                    const isCurrentDraggingOver = snapshot.isDraggingOver
                                                        || (group?.children?.isEmpty && isDragging);
                                                    if (isCurrentDraggingOver) {
                                                        classes.push("border alert alert-light");
                                                    }

                                                    const allowedChildrenInput: ReplacementGroup[] = [];
                                                    if (item.action?.allowedChildren?.isNotEmpty) {
                                                        item.action?.allowedChildren
                                                            .forEach(allowedActionChild => {
                                                                if (allowedActionChild.output?.isNotEmpty) {
                                                                    const { children, creator } = createChildren();
                                                                    allowedActionChild.output.forEach(creator);
                                                                    allowedChildrenInput.push({
                                                                        id: item?.id,
                                                                        label: item.action?.name as string,
                                                                        description: item.action?.description ?? "",
                                                                        input: children,
                                                                    });
                                                                }
                                                            });
                                                    }
                                                    return (
                                                        <div
                                                            className={`${classes.join(" ")}`}
                                                            ref={dragChild.innerRef}
                                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                                            {...dragChild.droppableProps}
                                                        >
                                                            {group?.children?.map((childItem, childIndex) => {
                                                                const groupChildren = group?.children?.limit(childIndex)
                                                                    .mapFilter(groupChild => {
                                                                        const { children, creator } = createChildren();

                                                                        if (groupChild.action?.output?.isNotEmpty) {
                                                                            groupChild.action.output.forEach(creator);
                                                                        }

                                                                        if (groupChild.output?.isNotEmpty) {
                                                                            groupChild.output.forEach(creator);
                                                                        }

                                                                        return children.isEmpty ? undefined : {
                                                                            id: groupChild.id,
                                                                            label: groupChild.name,
                                                                            description: groupChild.description ?? "",
                                                                            input: children,
                                                                        } as ReplacementGroup;
                                                                    }) ?? [];

                                                                if (child.output?.isNotEmpty) {
                                                                    const { children, creator } = createChildren();
                                                                    child.output.forEach(creator);
                                                                    groupChildren.push({
                                                                        id: item?.id,
                                                                        label: item.action?.name as string,
                                                                        description: item.action?.description ?? "",
                                                                        input: children,
                                                                    });
                                                                }
                                                                const childrenInput = input.concat(
                                                                    allowedChildrenInput,
                                                                    groupChildren,
                                                                ).dedupe("id");

                                                                return (
                                                                    <WorkflowRow
                                                                        index={childIndex}
                                                                        key={childItem.id}
                                                                        item={childItem}
                                                                        level={level + 1}
                                                                        rootStep={rootStep}
                                                                        onUpdate={onUpdate}
                                                                        isDraggingOver={isCurrentDraggingOver}
                                                                        isDragging={isDragging}
                                                                        type={type}
                                                                        input={childrenInput}
                                                                        allowedActionIds={allowedChildrenIds}
                                                                        draggingId={draggingId}
                                                                        onEdit={() => handleEditChild(
                                                                            childItem,
                                                                            child,
                                                                            childrenInput,
                                                                        )}
                                                                        onDelete={() => handleDeleteChild(childItem, group)}
                                                                    />
                                                                );
                                                            })}

                                                            {isDragging
                                                                && (!group?.children || group?.children?.isEmpty)
                                                                && (
                                                                    <div className="text-light py-4 w-100" />
                                                                )}

                                                            {dragChild.placeholder}

                                                        </div>
                                                    );
                                                }}
                                            </Droppable>

                                        </div>
                                    );
                                })}
                            </div>
                        )}
                </div>

            </div>

        </>

    );

    return level === 0 ? (
        <div data-level={level} className={className}>
            {container}
            {cardCreate}
        </div>
    ) : (
        <Draggable
            key={item.id}
            index={index}
            draggableId={item.id}
        >
            {(provided /* snapshot */) => (
                <div
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...provided.draggableProps}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                    data-level={level}
                    className={`${isDraggingOver ? "" : Styles["left-line"]} pb-2 pt-2 ${className}`}
                >
                    {container}
                    {cardCreate}
                </div>
            )}
        </Draggable>
    );
}

WorkflowRow.defaultProps = {
    className: "",
    isDraggingOver: false,
};

export function createWorklowStep(item: WorkflowStep): WorkflowStepInput {
    return {
        id: item.id || undefined,
        actionId: item.actionId || undefined,
        serviceId: item.serviceId || undefined,
        name: item.name,
        description: item.description,
        allowedChildren: item.allowedChildren?.map(child => ({
            description: child.description,
            id: child.id,
            label: child.label,
            allowedActionIds: child.allowedActionIds,
            output: child.output?.map(out => ({
                id: out.id,
                description: out.description,
                name: out.name,
                type: out.type,
            })),
        })),
        parameters: item.parameters?.map(param => ({
            value: {
                fieldId: param.value.fieldId as string,
                type: param.value.type,
                boolean: param.value.boolean,
                date: param.value.date,
                displayValue: param.value.displayValue,
                number: param.value.number,
                options: param.value.options,
                string: param.value.string,
            },
            replacement: param.replacement?.map(replace => ({
                fieldId: replace.fieldId,
                groupId: replace.groupId,
                replacement: replace.replacement,
            })),
        })),
        output: item.output?.map(param => ({
            description: param.description,
            name: param.name,
            type: param.type,
            id: param.id,
        })),
        children: item.children?.map(child => ({
            groupId: child.groupId || undefined,
            children: child.children?.map(createWorklowStep),
        })),
    };
}

export function createInput(item: Workflow): WorkflowInput {
    return {
        name: item.name,
        description: item.description,
        iconUrl: item.iconUrl,
        steps: item.steps?.map(row => createWorklowStep(row)) ?? [],
    };
}

export function removeTypename(steps: WorkflowStep[]): WorkflowStep[] {
    return steps.map(step => {
        const copy = { ...step };
        // eslint-disable-next-line no-param-reassign
        delete copy.__typename;
        if (step.children) {
            copy.children = step.children.map(group => ({
                groupId: group.groupId,
                children: group.children ? removeTypename(group.children) : undefined,
            }));
        }
        copy.output = step.output?.map(value => ({ ...value }));
        copy.parameters = step.parameters?.map(param => {
            const paramCopy = {
                ...param,
            };
            delete paramCopy.__typename;
            paramCopy.value = { ...paramCopy.value };
            delete paramCopy.value.__typename;
            paramCopy.replacement = param.replacement?.map(replace => {
                const replaceCopy = { ...replace };
                delete replaceCopy.__typename;
                return replaceCopy;
            });

            return paramCopy;
        });

        return copy;
    });
}

function createEmpty({
    shouldShowCreatingRequest,
}: {
    shouldShowCreatingRequest?: boolean;
}): WorkflowStep[] {
    const workflow = [];
    workflow.push(ACTION_REQUEST_CREATED);

    if (shouldShowCreatingRequest) {
        workflow.push(ACTION_CREATING_REQUEST);
    }

    return workflow.map(row => ({
        ...row,
        children: [],
        parameters: [],
    }));
}

interface FlattenResult {
    groupId: string;
    id: string;
    children: WorkflowStep[];
    parent: WorkflowStep;
}

function flatten(steps: WorkflowStep[]): FlattenResult[] {
    return steps.flatMap(row => {
        const taken = row.children?.map(child => ({
            id: `${row.id}/${child.groupId}`,
            groupId: child.groupId,
            parent: row,
            children: child.children as WorkflowStep[],
        }));

        const children = row.children?.flatMap(group => (group.children ? flatten(group.children) : undefined)).filterNull();

        const childrenTaken = new Set(row.children?.toIdList("groupId"));
        const allowed = row.action?.allowedChildren?.filterMap(child => ({
            id: `${row.id}/${child.id}`,
            groupId: child.id,
            parent: row,
            children: [] as WorkflowStep[],
        }), child => !childrenTaken.has(child.id));

        return (taken as FlattenResult[] ?? []).concat(allowed ?? [], children ?? []);
    }).filterNull();
}

export interface WorkflowProperties {
    input?: ReplacementGroup[];
    onChange?: (workflow: WorkflowStep[]) => void;
    value?: WorkflowStep[];
    shouldShowCreatingRequest?: boolean;
    isModal?: boolean;
    onHideModal?: () => void;
    type?: ActionContextType
}

export default function WorkflowDisplay({
    input, onChange, value, shouldShowCreatingRequest, isModal, onHideModal, type,
}: WorkflowProperties): ReactElement {
    const [current, setCurrent] = useState<WorkflowStep[]>(() => createEmpty({ shouldShowCreatingRequest }));
    const [draggingContext, setDraggingContext] = useState<string>();
    const [draggingId, setDraggingId] = useState<string>();
    const inputGroups = useMemo(() => {
        const groups = input ? [...input] : [];
        const index = groups.findIndex(group => group.id === "urn:fq:form-field-group:request");
        const requestGroup = groups[index];
        if (requestGroup?.input) {
            const { children, creator } = createChildren();
            requestGroup.input.forEach(creator);
            groups[index] = {
                ...requestGroup,
                input: children,
            };
        }

        return groups;
    }, [input]);

    useEffect(() => {
        if (value?.isNotEmpty) {
            const workflows = removeTypename(value);
            if (workflows.length === 1 && shouldShowCreatingRequest) {
                workflows.unshift(ACTION_CREATING_REQUEST);
            }
            setCurrent(workflows);
        } else {
            setCurrent(createEmpty({ shouldShowCreatingRequest }));
        }
    }, [value]);

    const onDragEnd = (result: DropResult) => {
        const droppables = flatten(current);
        const destination = droppables.find(row => row.id === result.destination?.droppableId);
        const source = droppables.find(row => row.id === result.source.droppableId);

        setDraggingContext(undefined);
        setDraggingId(undefined);

        if (!source || !destination) {
            return;
        }

        const [sourceItem] = source.children.splice(result.source.index, 1);
        if (!sourceItem) {
            return;
        }

        if (destination.children.isEmpty) {
            destination.parent.children ??= [];
            destination.parent.children.push({
                groupId: destination.groupId,
                children: destination.children,
            });
        }
        destination.children.splice(result.destination?.index ?? 0, 0, sourceItem);

        const copyRemoved = removeTypename(current);
        setCurrent(copyRemoved);
        onChange?.(copyRemoved);
    };

    const display = current?.map((row, rowIndex) => (
        <DragDropContext
            nonce={window.NONCE}
            key={row.id || row.actionId}
            onDragEnd={onDragEnd}
            onBeforeCapture={start => {
                setDraggingContext(row.id);
                setDraggingId(start.draggableId);
            }}
        >
            <WorkflowRow
                key={row.id || row.actionId}
                item={row}
                index={rowIndex}
                level={0}
                rootStep={row}
                className="mb-3"
                isDragging={draggingContext === row.id}
                draggingId={draggingId}
                type={type ?? ACTION_MAP[row.actionId] ?? ActionContextType.Ticket}
                input={inputGroups ?? []}
                allowedActionIds={[]}
                onUpdate={() => {
                    const copy = Array.from(current);
                    setCurrent(copy);
                    onChange?.(copy);
                }}
                onDelete={() => {
                    // do nothin
                }}
                onEdit={() => {
                    // do
                    // nothin
                }}
            />
        </DragDropContext>
    ));

    return isModal ? (
        <Confirm
            shouldShow
            size="xl"
            fullscreen="lg-down"
            dialogClassName="full-size-container bg-white shadow border-0 rounded"
            keyboardContext="workflow"
            onHide={onHideModal}
            shouldShowFooter={false}
        >
            <>
                {onHideModal && (
                    <button
                        className="btn btn-none btn-lg p-0 position-absolute top-0 end-0 m-2"
                        type="button"
                        onClick={onHideModal}
                    >
                        <BiCollapseAlt className="mt-n1 me-1" />
                    </button>
                )}
                <div className="p-2">
                    {display}
                </div>
            </>
        </Confirm>
    ) : (
        <div className="w-100 px-3 overflow-auto">
            {display}
        </div>
    );
}

WorkflowDisplay.defaultProps = {
    isModal: false,
    input: undefined,
    onChange: undefined,
    value: undefined,
    shouldShowCreatingRequest: false,
    onHideModal: undefined,
    type: undefined,
};
